import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown, HelpCircle } from "lucide-react";

const primaryColor = "#43bd98";

const FAQ = () => {
	const { t } = useTranslation();
	const [openIndex, setOpenIndex] = useState(null);

	const faqs = t("restaurantFAQ.items", { returnObjects: true });

	const toggleFAQ = (index) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<section
			style={{
				padding: "5rem 2rem",
				background: "#fff",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div style={{ maxWidth: "800px", margin: "0 auto" }}>
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					viewport={{ once: true }}
					style={{ textAlign: "center", marginBottom: "3rem" }}
				>
					<div
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "8px",
							color: primaryColor,
							marginBottom: "0.5rem",
						}}
					>
						<HelpCircle size={20} />
						<span
							style={{
								fontSize: "0.9rem",
								fontWeight: 600,
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							{t("restaurantFAQ.badge")}
						</span>
					</div>
					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
							fontWeight: 800,
							color: "#1a1a1a",
							margin: 0,
						}}
					>
						{t("restaurantFAQ.title")}
					</h2>
				</motion.div>

				{/* FAQ Items */}
				<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
					{faqs.map((faq, index) => (
						<motion.div
							key={index}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.4, delay: index * 0.08 }}
							viewport={{ once: true }}
							style={{
								background: openIndex === index ? "#f8fefc" : "#fafafa",
								border: `1px solid ${
									openIndex === index ? primaryColor + "40" : "#e5e7eb"
								}`,
								borderRadius: "12px",
								overflow: "hidden",
								transition: "all 0.3s ease",
							}}
						>
							<button
								onClick={() => toggleFAQ(index)}
								style={{
									width: "100%",
									padding: "1.25rem 1.5rem",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									background: "transparent",
									border: "none",
									cursor: "pointer",
									textAlign: "left",
								}}
							>
								<span
									style={{
										fontSize: "0.95rem",
										fontWeight: 600,
										color: "#1f2937",
										paddingRight: "1rem",
									}}
								>
									{faq.question}
								</span>
								<motion.div
									animate={{ rotate: openIndex === index ? 180 : 0 }}
									transition={{ duration: 0.3 }}
									style={{
										flexShrink: 0,
										color: primaryColor,
									}}
								>
									<ChevronDown size={20} />
								</motion.div>
							</button>

							<AnimatePresence>
								{openIndex === index && (
									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: "auto", opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.3 }}
										style={{ overflow: "hidden" }}
									>
										<p
											style={{
												padding: "0 1.5rem 1.25rem",
												margin: 0,
												fontSize: "0.95rem",
												color: "#4b5563",
												lineHeight: 1.7,
											}}
										>
											{faq.answer}
										</p>
									</motion.div>
								)}
							</AnimatePresence>
						</motion.div>
					))}
				</div>

				{/* CTA after FAQ */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					viewport={{ once: true }}
					style={{
						marginTop: "2.5rem",
						textAlign: "center",
						padding: "1.5rem",
						background: "#f8fefc",
						borderRadius: "12px",
						border: `1px solid ${primaryColor}20`,
					}}
				>
					<p
						style={{
							fontSize: "0.95rem",
							color: "#374151",
							margin: 0,
							marginBottom: "1rem",
						}}
					>
						{t("restaurantFAQ.cta.text")}
					</p>
					<a
						href="https://wa.me/34608916357"
						target="_blank"
						rel="noopener noreferrer"
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "8px",
							background: "#25D366",
							color: "#fff",
							padding: "0.75rem 1.5rem",
							borderRadius: "8px",
							fontWeight: 600,
							fontSize: "0.95rem",
							textDecoration: "none",
							transition: "transform 0.2s",
						}}
					>
						{t("restaurantFAQ.cta.button")}
					</a>
				</motion.div>
			</div>
		</section>
	);
};

export default FAQ;
